import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, organizationLd, webSiteLd, placeLd, datasetLd } from '../seo';
import cities from '../data/cities.json';
import localitiesData from '../data/localities.json';

interface CityData {
  slug: string;
  name: string;
  state: string;
  lat: number;
  lng: number;
  tier: number;
}

interface LocalityData {
  slug: string;
  name: string;
  lat: number;
  lng: number;
  bbox: { sw_lat: number; sw_lng: number; ne_lat: number; ne_lng: number };
  osm_id: number;
}

interface LocalityStats {
  city: { slug: string; name: string; state: string };
  locality: { slug: string; name: string; lat: number; lng: number };
  stats: {
    listings: {
      count: number;
      avg_hourly: number | null;
      avg_daily: number | null;
      avg_monthly: number | null;
    };
    by_vehicle_type: Record<string, number>;
  } | null;
  last_snapshot: { snapshot_date: string; p25: number | null; p50: number | null; p75: number | null } | null;
}

export default function LocalityPage() {
  const { city: citySlug = '', locality: localitySlug = '' } = useParams<{ city: string; locality: string }>();
  const [city, setCity] = useState<CityData | null>(null);
  const [locality, setLocality] = useState<LocalityData | null>(null);
  const [stats, setStats] = useState<LocalityStats | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string>('');

  useEffect(() => {
    const c = (cities as CityData[]).find((x) => x.slug === citySlug);
    setCity(c ?? null);
    if (c) {
      const found = (localitiesData as { city: string; localities: LocalityData[] }[]).find(
        (g) => g.city === citySlug
      )?.localities.find((l) => l.slug === localitySlug);
      setLocality(found ?? null);
      if (found) {
        const url = `/api/stats/locality/${citySlug}/${localitySlug}?lat=${found.lat}&lng=${found.lng}`;
        fetch(url)
          .then((r) => r.json())
          .then((j) => {
            if (j?.data) setStats(j.data);
          })
          .catch(() => undefined);
      }
    }
    setUpdatedAt(new Date().toISOString());
  }, [citySlug, localitySlug]);

  if (!city || !locality) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-2xl font-bold text-gray-900">Locality not found</h1>
        <p className="text-gray-600 mt-2">
          We don't have data for <code>{citySlug}/{localitySlug}</code> yet.
        </p>
        <Link to={`/${citySlug}`} className="text-parksy-600 hover:underline mt-4 inline-block">
          Back to {city?.name ?? 'city'} →
        </Link>
      </div>
    );
  }

  const pageTitle = `Parking in ${locality.name}, ${city.name} — ${stats?.stats?.listings?.count ?? 0} spots | Parksy`;
  const description = `Find parking in ${locality.name}, ${city.name}. Real prices and live availability from neighbours. ${stats?.stats?.listings?.avg_monthly ? `Average monthly rate: ₹${Math.round(stats.stats.listings.avg_monthly).toLocaleString('en-IN')}.` : ''}`;
  const canonical = `https://parksy.rent/${citySlug}/${localitySlug}`;

  return (
    <>
      <Breadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: `Parking in ${city.name}`, to: `/${citySlug}` },
          { label: locality.name },
        ]}
        pageTitle={pageTitle}
        pageDescription={description}
        extraSchemas={[
          organizationLd(),
          webSiteLd(),
          placeLd({
            name: `${locality.name}, ${city.name}`,
            description: `Parking data for ${locality.name}, ${city.name}, ${city.state}.`,
            citySlug: citySlug,
            localitySlug: localitySlug,
            lat: locality.lat,
            lng: locality.lng,
          }),
          datasetLd({
            name: `${locality.name} parking stats`,
            description: `Live parking stats and average rates for ${locality.name}, ${city.name}.`,
            url: canonical,
            citySlug: citySlug,
            localitySlug: localitySlug,
            updatedAt: updatedAt,
            rowCount: stats?.stats?.listings?.count,
          }),
        ]}
      />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Parking in {locality.name}</h1>
          <p className="text-gray-600">
            {city.name}, {city.state}. Live data from neighbours.
          </p>
        </header>

        {stats?.stats?.listings?.count ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-parksy-600">{stats.stats.listings.count}</div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">Spots</div>
              </div>
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {stats.stats.listings.avg_monthly ? `₹${Math.round(stats.stats.listings.avg_monthly).toLocaleString('en-IN')}` : '—'}
                </div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">Avg ₹/month</div>
              </div>
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {stats.stats.listings.avg_daily ? `₹${Math.round(stats.stats.listings.avg_daily).toLocaleString('en-IN')}` : '—'}
                </div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">Avg ₹/day</div>
              </div>
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-amber-600">
                  {stats.stats.listings.avg_hourly ? `₹${Math.round(stats.stats.listings.avg_hourly).toLocaleString('en-IN')}` : '—'}
                </div>
                <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">Avg ₹/hour</div>
              </div>
            </div>

            {Object.keys(stats.stats.by_vehicle_type ?? {}).length > 0 && (
              <div className="bg-white border rounded-xl p-5 mb-8">
                <h2 className="font-semibold text-gray-900 mb-2">Vehicle type breakdown</h2>
                <div className="flex flex-wrap gap-2 text-sm">
                  {Object.entries(stats.stats.by_vehicle_type).map(([vt, count]) => (
                    <span key={vt} className="bg-gray-50 border rounded-lg px-3 py-1 text-gray-700">
                      {vt}: <strong>{count}</strong>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <h2 className="font-semibold text-amber-900 mb-1">No data yet for {locality.name}</h2>
            <p className="text-amber-800 text-sm mb-3">
              Be the first to drop a pin and unlock price data for the rest of the neighbourhood.
            </p>
            <Link
              to="/"
              className="inline-block bg-parksy-600 hover:bg-parksy-700 text-white font-medium px-4 py-2 rounded-lg text-sm"
            >
              Open the map →
            </Link>
          </div>
        )}

        <div className="flex flex-wrap gap-3 mb-10">
          <Link
            to="/"
            className="inline-block bg-parksy-600 hover:bg-parksy-700 text-white font-medium px-5 py-2.5 rounded-lg"
          >
            Open the map
          </Link>
          <Link
            to="/list-your-parking"
            className="inline-block bg-white border border-parksy-600 text-parksy-700 hover:bg-parksy-50 font-medium px-5 py-2.5 rounded-lg"
          >
            List your spot
          </Link>
        </div>

        <p className="text-xs text-gray-500">
          Data sourced from {stats?.stats?.listings?.count ?? 0} crowdsourced listings within
          ~2.5 km of {locality.name}. Updated continuously.
        </p>
      </div>
    </>
  );
}
